// ===================================
// TOPIC FORM COMPONENT - src/components/TopicForm.tsx
// ===================================
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import TopicsApi from '../services/topicsApi';
import { parseUserPermissions, getDefaultBusinessId } from '../utils/permissions';
import { Topic, CreateTopicRequest, UpdateTopicRequest, TopicFormData, TopicFormErrors } from '../types';
import { X, Save, Loader2 } from 'lucide-react';
import Input from './ui/input';
import Label from './ui/label';
import Button from './ui/button';

interface TopicFormProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  topic?: Topic | null;
  onClose: () => void;
  onSuccess: (topic: Topic) => void;
}

const TopicForm: React.FC<TopicFormProps> = ({
  isOpen,
  mode,
  topic,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const permissions = parseUserPermissions(user);

  // Form state
  const [formData, setFormData] = useState<TopicFormData>({
    name: '',
    description: '',
    business_id: getDefaultBusinessId(user)
  });
  const [errors, setErrors] = useState<TopicFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when topic changes
  useEffect(() => {
    if (mode === 'edit' && topic) {
      setFormData({
        name: topic.name,
        description: topic.description || '',
        business_id: topic.business_id
      });
    } else {
      setFormData({
        name: '',
        description: '',
        business_id: getDefaultBusinessId(user)
      });
    }
    setErrors({});
  }, [mode, topic, user]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: TopicFormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Topic name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Topic name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Topic name must be less than 100 characters';
    }

    // Description validation
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    // Remove business ID validation - it's now optional

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field: keyof TopicFormData, value: string | number | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear specific field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Handle form submission
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }

  setIsSubmitting(true);

  try {
    let savedTopic: Topic;

    if (mode === 'create') {
      const createData: CreateTopicRequest = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        business_id: formData.business_id
      };

      // ADD THIS DEBUG LOG
      console.log('=== CREATING TOPIC ===');
      console.log('Form data:', formData);
      console.log('Create payload:', createData);
      console.log('business_id value:', createData.business_id);
      console.log('business_id type:', typeof createData.business_id);

      savedTopic = await TopicsApi.createTopic(createData);

        addNotification({
          type: 'success',
          title: 'Topic created',
          message: `Topic "${savedTopic.name}" has been created successfully`
        });
      } else {
        if (!topic) throw new Error('Topic not found');

        const updateData: UpdateTopicRequest = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          business_id: formData.business_id
        };

        savedTopic = await TopicsApi.updateTopic(topic.id, updateData);

        addNotification({
          type: 'success',
          title: 'Topic updated',
          message: `Topic "${savedTopic.name}" has been updated successfully`
        });
      }

      onSuccess(savedTopic);
      onClose();

    } catch (err) {
      const error = TopicsApi.handleApiError(err);
      addNotification({
        type: 'error',
        title: `Failed to ${mode} topic`,
        message: error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {mode === 'create' ? 'Create New Topic' : 'Edit Topic'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Topic Name */}
              <div>
                <Label htmlFor="topic-name">
                  Topic Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="topic-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter topic name"
                  className={errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="topic-description">Description</Label>
                <textarea
                  id="topic-description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter topic description (optional)"
                  className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50 ${
                    errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  disabled={isSubmitting}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  {formData.description.length}/500 characters
                </p>
              </div>

            {/* Business Selection - Now optional for all users */}
<div>
  <Label htmlFor="topic-business">Business</Label>
  <select
    id="topic-business"
    value={formData.business_id ?? ''}
    onChange={(e) => {
      const value = e.target.value;
      handleInputChange('business_id', value === '' ? null : parseInt(value));
    }}
    className={`block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50 ${
      errors.business_id ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
    }`}
    disabled={isSubmitting}
  >
    <option value="">Global (All Businesses)</option>
    {user?.business_id && (
      <option value={user.business_id}>
        {user.current_business} (Current Business)
      </option>
    )}
    {permissions.isGodMode && (
      <>
        <option disabled>─────────────</option>
        <option value="1">Business 1</option>
        <option value="2">Business 2</option>
        <option value="3">Business 3</option>
        {/* Add more businesses dynamically as needed */}
      </>
    )}
  </select>
  {errors.business_id && (
    <p className="mt-1 text-sm text-red-600">{errors.business_id}</p>
  )}
  <p className="mt-1 text-sm text-gray-500">
    {formData.business_id === null || formData.business_id === undefined
      ? 'This topic will be available globally across all businesses'
      : `This topic will only be available to Business ${formData.business_id}`
    }
  </p>
</div>
            </form>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full sm:w-auto sm:ml-3"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'create' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {mode === 'create' ? 'Create Topic' : 'Update Topic'}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="mt-3 w-full sm:mt-0 sm:w-auto"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicForm;